import { useMemo } from 'react';

import { Tooltip } from '@/design-system';
import { CommitWithLookupsSchema, RepoCommitTag } from '@/types/schema';

import { useVisibleTags } from '../hooks/useVisibleTags';
import AddCommitTagButton from './AddCommitTagButton';

const CommitTag = ({ tagName }: { tagName: string }) => {
  return (
    <div className="flex items-center justify-center gap-1.5 rounded-md bg-tertiary px-1.5 py-1 text-xs font-medium text-primary">
      <span className="max-w-[100px] overflow-hidden truncate">{tagName}</span>
    </div>
  );
};

const CommitTags = ({
  tags,
  commit,
  containerRef,
  owner,
  repo,
}: {
  tags: RepoCommitTag[];
  commit: CommitWithLookupsSchema;
  containerRef: React.RefObject<HTMLDivElement>;
  owner?: string;
  repo?: string;
}) => {
  const { visibleTags, overflowTags } = useVisibleTags(
    tags,
    containerRef,
    commit.commit_hash
  );

  // Force component to update when tags change
  const tagsList = useMemo(() => {
    return visibleTags.map((tag) => (
      <CommitTag key={`${tag.id}-${tag.tag_name}`} tagName={tag.tag_name} />
    ));
  }, [visibleTags]);

  const overflowTooltip = useMemo(() => {
    if (!overflowTags.length) return null;

    return (
      <Tooltip
        title={
          <div className="flex flex-col gap-1">
            {overflowTags.map((tag) => (
              <span key={`overflow-${tag.id}`}>{tag.tag_name}</span>
            ))}
          </div>
        }
        tooltipClassName="max-w-[300px]"
      >
        <div>
          <CommitTag tagName={`+ ${overflowTags.length}`} />
        </div>
      </Tooltip>
    );
  }, [overflowTags]);

  return (
    <div className="flex cursor-default items-center gap-2">
      {tagsList}
      {overflowTooltip}
      <AddCommitTagButton commit={commit} owner={owner} repo={repo} />
    </div>
  );
};

export default CommitTags;
